// fleet: the device and node registry (catalog-outline.md "fleet", CON-03; ops plan 2026-10-11-device-records). A device
// is a record (name, form factor, Terminal ID, billing) that holds 0..n keys: a unit is activated onto a record with the
// 8-digit code it shows, and replacing a broken unit activates the new one onto the same record, so its Terminal ID,
// settings and billing carry over. Records and keys are `fleet.device` config facts (ADR-015), which replicate to the
// location's node so it verifies the device's proofs offline. The Terminal ID is a secret: it never enters a fact.
import { z } from 'zod';
import { QueueHealthView } from '../ops/queues.ts';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { defineError } from '../../define/errors.ts';
import { defineEvent } from '../../define/event.ts';
import { additive } from '../../define/upcast.ts';
import { secret } from '../../define/secret.ts';
import { ConfigFieldsWritten } from '../config/index.ts';
import { CommanderCommandName, DeviceAlert, DeviceCommandName, DeviceInfoKey } from '../../sync/protocol.ts';
import { CardNumberInText, SupportCaseClosed, SupportCaseNotFound } from '../ops/support.ts';
import { DeviceId, IsoInstant, LocalDate, LocationId } from '../../primitives/codecs.ts';

const FormFactor = z.enum(['register', 'handheld', 'kds', 'menu_display', 'customer_display']).meta({ id: 'FormFactor' });

/** An activation code as the device shows it: 8 digits, so it can be read out over the phone (legacy parity). */
const ActivationCode = secret(z.string().regex(/^\d{8}$/));

/** A payment terminal's id (Datacap TID, Clearent API key): a credential, staff only, delivered only to its device. */
const TerminalId = secret(z.string().trim().min(1).max(100));

export const ClaimCodeInvalid = defineError({
  code: 'fleet.claim_code_invalid', httpStatus: 404, message: 'errors.fleet.claim_code_invalid', retryable: false,
  details: z.object({}),
});
export const ClaimCodeExpired = defineError({
  code: 'fleet.claim_code_expired', httpStatus: 410, message: 'errors.fleet.claim_code_expired', retryable: false,
  details: z.object({ expiredAt: IsoInstant }),
});
/** Too many wrong registration codes from one person: wait before trying again. */
export const ClaimCodeLocked = defineError({
  code: 'fleet.claim_code_locked', httpStatus: 429, message: 'errors.fleet.claim_code_locked', retryable: true,
  details: z.object({ retryAfterSeconds: z.int().positive() }),
});
/** The code's unit was refused by someone at the store: it must start over with a new code. */
export const ClaimDenied = defineError({
  code: 'fleet.claim_denied', httpStatus: 410, message: 'errors.fleet.claim_denied', retryable: false,
  details: z.object({}),
});
/** The unit asks to run as something the chosen record can't (a kitchen display onto a register record, and so on). */
export const ClaimFormFactorMismatch = defineError({
  code: 'fleet.claim_form_factor_mismatch', httpStatus: 409, message: 'errors.fleet.claim_form_factor_mismatch', retryable: false,
  details: z.object({ unitFormFactor: FormFactor, recordFormFactor: FormFactor }),
});
/**
 * B996: a record's form factor decides what its units may do acting as themselves (KDS-P-01), so it may change only to a
 * kind every live unit on it could have been activated onto (core `unitCanActivateOnto`). A unit of another kind is
 * revoked first and activated again onto the right record.
 */
export const FormFactorHeldByUnit = defineError({
  code: 'fleet.form_factor_held_by_unit', httpStatus: 409, message: 'errors.fleet.form_factor_held_by_unit', retryable: false,
  details: z.object({ keyId: z.uuid(), unitFormFactor: FormFactor, recordFormFactor: FormFactor }),
});
export const DeviceKeyNotFound = defineError({
  code: 'fleet.device_key_not_found', httpStatus: 404, message: 'errors.fleet.device_key_not_found', retryable: false,
  details: z.object({ deviceId: DeviceId, keyId: z.uuid() }),
});
export const DeviceNotFound = defineError({
  code: 'fleet.device_not_found', httpStatus: 404, message: 'errors.fleet.device_not_found', retryable: false,
  details: z.object({ deviceId: DeviceId }),
});

/** Commander's Alert needs the staff member to have stepped up in this session within five minutes (core `isSteppedUp`). */
export const StepUpRequired = defineError({
  code: 'fleet.step_up_required', httpStatus: 403, message: 'errors.fleet.step_up_required', retryable: false,
  details: z.object({}),
});

/** At most ALERT_RATE.perMinute alert sends a minute per location, so a stuck script can't flood a store's screens. */
export const AlertRateLimited = defineError({
  code: 'fleet.alert_rate_limited', httpStatus: 429, message: 'errors.fleet.alert_rate_limited', retryable: true,
  details: z.object({ perMinute: z.int().positive() }),
});

/**
 * The location's device switch-over (D-23) cannot happen yet: these records have no live unit. Every device at a
 * location moves at once, so a device that cannot switch blocks the cutover (activate a unit onto it, or delete it).
 */
export const DevicesNotReady = defineError({
  code: 'fleet.devices_not_ready', httpStatus: 409, message: 'errors.fleet.devices_not_ready', retryable: false,
  details: z.object({ deviceIds: z.array(DeviceId) }),
});
/**
 * After a rollback, these units must be reset (clear the app, activate it again) before the location switches over:
 * each key was activated before its origin's newest drain hold, so its outbox may still hold a sale rung after the
 * freeze that the GM re-entered in legacy, which would be counted twice (cutover runbook §5; core judgeSwitchOverHolds).
 */
export const ResetRequired = defineError({
  code: 'fleet.reset_required', httpStatus: 409, message: 'errors.fleet.reset_required', retryable: false,
  details: z.object({ devices: z.array(z.object({ deviceId: DeviceId, deviceName: z.string(), keyClaimedAt: IsoInstant, heldSince: IsoInstant })) }),
});
/**
 * After a rollback, drain holds still wait at the location: not discarded (`migrate held-review`, then `held-discard`),
 * or discarded but still listed by a node that has not heard it, or counted by a receiver that does not list them all.
 */
export const DrainHoldsWaiting = defineError({
  code: 'fleet.drain_holds_waiting', httpStatus: 409, message: 'errors.fleet.drain_holds_waiting', retryable: false,
  details: z.object({ notDiscarded: z.array(z.string()), unheard: z.array(z.string()), unitemized: z.array(z.string()) }),
});
/** The location's devices were switched over to Radish Next; it can't be held for a cutover again. */
export const LocationSwitchedOver = defineError({
  code: 'fleet.location_switched_over', httpStatus: 409, message: 'errors.fleet.location_switched_over', retryable: false,
  details: z.object({ locationId: LocationId, switchedAt: IsoInstant }),
});

export const DeviceClaimed = defineEvent({
  name: 'DeviceClaimed',
  // v2 (device records): the customer display joined the form factors, so every v1 payload reads as v2 unchanged.
  version: 2,
  summary: 'A device was claimed to a location with a registration code.',
  aggregate: 'device',
  payload: z.object({ deviceId: DeviceId, locationId: LocationId, deviceName: z.string(), formFactor: FormFactor, claimedAt: IsoInstant }),
  verifiable: ['deviceId', 'locationId'],
  merge: 'idempotent_set',
  deviceConsumed: false,
  upcastFrom: [additive(1)],
});

export const DeviceRevoked = defineEvent({
  name: 'DeviceRevoked',
  version: 1,
  summary: 'A device lost its credentials; its queued events are quarantined.',
  aggregate: 'device',
  payload: z.object({ deviceId: DeviceId, reason: z.string(), revokedAt: IsoInstant }),
  verifiable: ['deviceId'],
  merge: 'lifecycle',
  deviceConsumed: true,
});

export const ClaimDevice = defineCommand({
  name: 'fleet.claimDevice',
  summary: 'Activate a unit onto a device record with the 8-digit code it shows (staff may also create the record).',
  input: z.object({
    /** The record the unit is activated onto. A new id (minted by the caller, UUIDv7) creates the record: staff only. */
    deviceId: DeviceId,
    claimCode: ActivationCode,
    locationId: LocationId,
    /** The record's name and form factor; used only when the claim creates the record. */
    deviceName: z.string().min(1).max(60),
    formFactor: FormFactor,
  }),
  output: z.object({ deviceId: DeviceId, locationId: LocationId, deviceName: z.string(), formFactor: FormFactor, claimedAt: IsoInstant }),
  permission: 'hardware.manage',
  risk: 'high',
  availability: 'location',
  origin: 'any',
  emits: [ConfigFieldsWritten],
  errors: [ClaimCodeInvalid, ClaimCodeExpired, ClaimCodeLocked, ClaimDenied, ClaimFormFactorMismatch, DeviceNotFound],
  dryRun: true,
  agent: { expose: false },
  legacy: ['POST /v1/device-claims/{id}/approve', 'POST /v1/devices/register'],
});

/** A pending activation as the person entering its code sees it, before they pick the record. */
export const ClaimView = z
  .object({
    formFactor: FormFactor,
    hardwareModel: z.string().nullable(),
    expiresAt: IsoInstant,
    /** The record this install was last activated onto (matched by its install digest), if any. */
    previousDevice: z.object({ deviceId: DeviceId, deviceName: z.string(), locationId: LocationId }).nullable(),
  })
  .meta({ id: 'ClaimView' });

export const LookupClaim = defineCommand({
  name: 'fleet.lookupClaim',
  summary: 'Look up the unit an activation code belongs to (wrong codes count toward the lockout).',
  input: z.object({ claimCode: ActivationCode, locationId: LocationId }),
  output: ClaimView,
  permission: 'hardware.manage',
  risk: 'medium',
  availability: 'location',
  origin: 'any',
  emits: [],
  errors: [ClaimCodeInvalid, ClaimCodeExpired, ClaimCodeLocked, ClaimDenied],
  dryRun: true,
  agent: { expose: false },
  legacy: ['GET /v1/device-claims?code={code}'],
});

export const DenyClaim = defineCommand({
  name: 'fleet.denyClaim',
  summary: 'Refuse a pending activation: the unit shows that it was denied and must start over.',
  input: z.object({ claimCode: ActivationCode, locationId: LocationId }),
  output: z.object({ deniedAt: IsoInstant }),
  permission: 'hardware.manage',
  risk: 'medium',
  availability: 'location',
  origin: 'any',
  emits: [],
  errors: [ClaimCodeInvalid, ClaimCodeExpired, ClaimCodeLocked, ClaimDenied],
  dryRun: true,
  agent: { expose: false },
  legacy: ['POST /v1/device-claims/{id}/deny'],
});

const DeviceOutput = z.object({ deviceId: DeviceId, locationId: LocationId, deviceName: z.string(), formFactor: FormFactor });

export const CreateDevice = defineCommand({
  name: 'fleet.createDevice',
  summary: 'Create a device record (name, form factor, Terminal ID, billing) for a unit to be activated onto. Staff only.',
  input: z.object({
    /** Minted by the caller (UUIDv7) so a retried create replays. */
    deviceId: DeviceId,
    locationId: LocationId,
    deviceName: z.string().trim().min(1).max(60),
    formFactor: FormFactor,
    terminalId: TerminalId.optional(),
    /** Counted in subscription billing (default true). Stored and shown only for now (billing plan). */
    billable: z.boolean().default(true),
  }),
  output: DeviceOutput,
  permission: 'settings.platform.manage',
  risk: 'high',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: 'Device records carry the Terminal ID and billing, which only Radish staff manage, in the cloud.',
  emits: [ConfigFieldsWritten],
  errors: [],
  dryRun: true,
  agent: { expose: false },
  legacy: ['POST /v1/devices'],
});

export const EditDevice = defineCommand({
  name: 'fleet.editDevice',
  summary: 'Rename a device record or change its form factor (only to one its live units can run as); staff also set its Terminal ID and billing.',
  input: z.object({
    deviceId: DeviceId,
    locationId: LocationId,
    deviceName: z.string().trim().min(1).max(60).optional(),
    formFactor: FormFactor.optional(),
    /** Staff only. Null clears it. */
    terminalId: TerminalId.nullable().optional(),
    /** Staff only (Andrew 2026-10-11, ledger L167). */
    billable: z.boolean().optional(),
  }),
  output: DeviceOutput,
  permission: 'hardware.manage',
  risk: 'high',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: 'The Terminal ID is sealed with a cloud-held key and billing is the cloud\'s; a rename rides along.',
  emits: [ConfigFieldsWritten],
  errors: [DeviceNotFound, FormFactorHeldByUnit],
  dryRun: true,
  agent: { expose: false },
  legacy: ['PUT /v1/devices/{id}'],
});

export const DeleteDevice = defineCommand({
  name: 'fleet.deleteDevice',
  summary: 'Delete a device record; every unit activated onto it loses its credentials. Staff only.',
  input: z.object({ deviceId: DeviceId, locationId: LocationId }),
  output: z.object({ deviceId: DeviceId, deletedAt: IsoInstant }),
  permission: 'settings.platform.manage',
  risk: 'high',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: 'Revocation must come from the authority that issues device tokens so every node and device sees it.',
  emits: [ConfigFieldsWritten],
  errors: [DeviceNotFound],
  dryRun: true,
  agent: { expose: false },
  legacy: ['DELETE /v1/devices/{id}'],
});

export const RevokeDeviceKey = defineCommand({
  name: 'fleet.revokeDeviceKey',
  summary: "Remove one unit's credentials from a device record; the record and its other units stay.",
  input: z.object({ deviceId: DeviceId, locationId: LocationId, keyId: z.uuid(), reason: z.string().min(1).max(200) }),
  output: z.object({ deviceId: DeviceId, keyId: z.uuid(), revokedAt: IsoInstant }),
  permission: 'hardware.manage',
  risk: 'high',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: 'Revocation must come from the authority that issues device tokens so every node and device sees it.',
  emits: [ConfigFieldsWritten],
  errors: [DeviceNotFound, DeviceKeyNotFound],
  dryRun: true,
  agent: { expose: false },
  legacy: ['DELETE /v1/devices/{id}/credentials/{credentialId}'],
});

export const RevokeDevice = defineCommand({
  name: 'fleet.revokeDevice',
  summary: 'Revoke a device so it can no longer sign in or sync.',
  input: z.object({ deviceId: DeviceId, locationId: LocationId, reason: z.string().min(1).max(200) }),
  output: z.object({ deviceId: DeviceId, revokedAt: IsoInstant }),
  permission: 'hardware.manage',
  risk: 'high',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: 'Revocation must come from the authority that issues device tokens so every node and device sees it.',
  emits: [ConfigFieldsWritten],
  errors: [DeviceNotFound],
  dryRun: true,
  agent: { expose: true, surfaces: ['commander.support'] },
});

export const HoldDevicesForCutover = defineCommand({
  name: 'fleet.holdDevicesForCutover',
  summary: "Hold a migrating location's devices: units are activated onto its records, but none signs in until the switch-over.",
  input: z.object({ locationId: LocationId }),
  output: z.object({ locationId: LocationId, heldAt: IsoInstant }),
  permission: 'settings.platform.manage',
  risk: 'critical',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: 'The migration holds a location in the cloud, the authority every node and device hears (D-23: no mixed fleet).',
  emits: [ConfigFieldsWritten],
  errors: [LocationSwitchedOver],
  dryRun: true,
  agent: { expose: false },
});

/**
 * The location's reader-gated features (WF-40 round 3; contracts sync/capabilities.ts), config entity
 * `fleet.location_capabilities`, id = the location id. Each server writes only its own view.
 */
export const LocationCapabilitiesFields = z
  .object({
    locationId: LocationId,
    /** The features every active register reads, as the location node saw their pulls. */
    atNode: z.array(z.string().min(1).max(64)).max(64),
    /** The same, as the cloud saw the pulls of registers on their cloud route. */
    atCloud: z.array(z.string().min(1).max(64)).max(64),
  })
  .partial()
  .meta({ id: 'LocationCapabilitiesFields' });

export const RefreshCapabilities = defineCommand({
  name: 'fleet.refreshCapabilities',
  summary:
    "Work out which reader-gated features (Combine Orders) every register active at the location reads, from the pulls this server answered, and write this server's view for the node and registers when it changed.",
  input: z.object({ locationId: LocationId }),
  output: z.object({ locationId: LocationId, available: z.array(z.string()) }),
  permission: 'hardware.manage',
  risk: 'low',
  availability: 'location',
  origin: 'any',
  emits: [ConfigFieldsWritten],
  errors: [],
  dryRun: true,
  agent: { expose: false },
});

export const SwitchOverDevices = defineCommand({
  name: 'fleet.switchOverDevices',
  summary: "Switch a held location's devices over at cutover: every unit activated onto its records signs in at once.",
  input: z.object({ locationId: LocationId }),
  output: z.object({ locationId: LocationId, switchedAt: IsoInstant, devices: z.int().nonnegative() }),
  permission: 'settings.platform.manage',
  risk: 'critical',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: 'The cutover switches a location in the cloud, the authority every node and device hears (D-23: all devices at once).',
  emits: [ConfigFieldsWritten],
  errors: [DevicesNotReady, ResetRequired, DrainHoldsWaiting],
  dryRun: true,
  agent: { expose: false },
});

/**
 * Back to legacy after a cutover (migration plan §4.16): the account's system of record returns to legacy, so its
 * location's units must stop working on Radish Next at once. The units keep their activation (their keys stay live),
 * so the next cutover's switch-over releases them again without anyone re-entering a code. A location that was never
 * switched over stays held as it is.
 */
export const HoldDevicesAgain = defineCommand({
  name: 'fleet.holdDevicesAgain',
  summary: "Hold a switched-over location's devices again when its account goes back to legacy: units keep their activation but none signs in until the next switch-over.",
  input: z.object({ locationId: LocationId }),
  output: z.object({ locationId: LocationId, heldAt: IsoInstant, wasSwitchedAt: IsoInstant.nullable() }),
  permission: 'settings.platform.manage',
  risk: 'critical',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: 'Going back to legacy holds a location in the cloud, the authority every node and device hears (D-23: no mixed fleet).',
  emits: [ConfigFieldsWritten],
  errors: [],
  dryRun: true,
  agent: { expose: false },
});

/**
 * After a rollback, the GM re-enters each reviewed post-freeze sale in legacy by hand (cutover runbook §5); this discards
 * those sales on Radish Next so they are never counted twice (lane C's `migrate held-discard`). The drain-held rows are
 * kept and stay uncounted; the location's node hears the discard through the feed and does the same, its beacon stops
 * reporting them, and any receiver that is ever pushed one of these commands again (a unit switched over without the
 * reset) counts it as seen without applying it (`discarded_after_rollback`) and raises `sync_discard_replayed`.
 */
export const DiscardDrainHolds = defineCommand({
  name: 'fleet.discardDrainHolds',
  summary: 'Discard reviewed drain-held sales (re-entered in legacy after a rollback) so no receiver ever counts them.',
  input: z.object({
    locationId: LocationId,
    discards: z.array(z.object({
      commandId: z.uuid(),
      reviewRef: z.string().min(1).max(200),
      // The held command's origin and when its receiver held it (lane C): `fleet.switchOverDevices` refuses a unit of
      // that origin activated before it, even once the receiver no longer lists the discarded command.
      origin: z.string().min(1).max(80).optional(),
      heldAt: IsoInstant.optional(),
    })).min(1).max(500),
  }),
  output: z.object({ locationId: LocationId, discarded: z.int().nonnegative() }),
  permission: 'settings.platform.manage',
  risk: 'critical',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: "The rollback review is the cloud's record; the node hears the discard through the location feed.",
  emits: [ConfigFieldsWritten],
  errors: [],
  dryRun: true,
  agent: { expose: false },
});

/**
 * A160 Decided 2, step 4 (b) (ops plan 2026-10-20-order-numbering): when numbering at a location moves from the cloud to
 * its hub (the cutover's node authority step), the cloud hands over the last order number it issued there on the
 * current business date, as a cloud-only config fact (`fleet.location_order_numbering`, id = the location id). The hub
 * hears it through the location feed and raises its carry-over floor to it, so its counter goes on from there even past
 * numbers the cloud gave out and no order used (a gap the feed alone never shows). Its beacon then reports that count
 * (`orderNumbers`), and the node authority step waits for it instead of refusing every day the cloud numbered orders.
 * Registers never receive the fact (no device view).
 */
export const HandOverOrderNumbering = defineCommand({
  name: 'fleet.handOverOrderNumbering',
  summary: "Hand the cloud's order-number count for the business date over to the location's hub, so it numbers on from there.",
  input: z.object({ locationId: LocationId, nodeId: z.uuid() }),
  output: z.object({ locationId: LocationId, businessDate: LocalDate, lastNumber: z.int().nonnegative() }),
  permission: 'settings.platform.manage',
  risk: 'critical',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: "Only the cloud knows the numbers it issued; the hub hears the hand-over through the location feed.",
  emits: [ConfigFieldsWritten],
  errors: [],
  dryRun: true,
  agent: { expose: false },
});

/**
 * One unit's credentials on a record (Manage Credentials). `lastSeenAt` is when the unit last authenticated with the key
 * at the server answering (legacy `last_used_at`, stamped at most once a minute); null until it has.
 */
export const DeviceKeyView = z
  .object({ keyId: z.uuid(), formFactor: FormFactor, hardwareModel: z.string().nullable(), claimedAt: IsoInstant, lastSeenAt: IsoInstant.nullable() })
  .meta({ id: 'DeviceKeyView' });

/** A unit's revoked credentials, kept so Manage Credentials shows the record's history (legacy lists every credential ever issued). */
export const RevokedDeviceKeyView = DeviceKeyView.extend({ revokedAt: IsoInstant }).meta({ id: 'RevokedDeviceKeyView' });

/**
 * A device record that has not been deleted or revoked, as the backoffice lists it (POS Devices, Category Visibility's
 * registers, legacy `GET /v1/devices`). The Terminal ID shows only its last 4 characters; `claimedAt` is when the
 * record was created or first claimed; `keys` are its live units, `revokedKeys` the credentials revoked on it.
 */
export const DeviceView = z
  .object({
    deviceId: DeviceId, locationId: LocationId, deviceName: z.string(), formFactor: FormFactor, claimedAt: IsoInstant,
    terminalIdLast4: z.string().max(4).nullable(), billable: z.boolean(), keys: z.array(DeviceKeyView),
    // Optional, so a back office reading an older server's answer (none listed) still parses it.
    revokedKeys: z.array(RevokedDeviceKeyView).optional(),
  })
  .meta({ id: 'DeviceView' });

export const ListDevices = defineQuery({
  name: 'fleet.devices.list',
  summary: "The location's device records that have not been deleted or revoked, by name, with their live units and revoked credentials.",
  input: z.object({ locationId: LocationId }),
  output: z.object({ devices: z.array(DeviceView) }),
  permission: { session: 'user' },
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['GET /v1/devices', 'GET /v1/categories/device-visibility'],
});

/**
 * A location node's newest health beacon as the cloud received it (location-node plan §4.9, the plan's
 * `fleet.nodeHealth`): its alarms and disk use, so a person sees what the node raised (staging, 2026-10-14: the node's
 * WAL archive filled its disk with `disk_high` raised for hours and read by nothing). `beaconAt` is null for a node
 * that has not sent one yet.
 */
export const NodeHealthView = z
  .object({
    nodeId: z.uuid(), serial: z.string(), claimedAt: IsoInstant.nullable(), beaconAt: IsoInstant.nullable(), receivedAt: IsoInstant.nullable(),
    alarms: z.array(z.string()), disk: z.array(z.object({ mount: z.string(), sizeBytes: z.int().nonnegative(), freeBytes: z.int().nonnegative() })),
    /**
     * The node receiver's refusals (bug B357): corrupt items quarantined in the last 24 hours, and items held back for a
     * newer build with the oldest one's age. Null when the newest beacon carries none (an agent before L515).
     */
    sync: z.object({ quarantinedLast24h: z.int().nonnegative(), held: z.int().nonnegative(), oldestHeldSeconds: z.number().nonnegative().nullable() }).nullable(),
    /**
     * Decisions the node made itself and lost with its disk (location-node plan §4.8 a): an origin the node decided
     * under before a restore from backup, whose last reported head the cloud never received. Each range is gone for
     * good (until peer escrow, plan §4.7 layer 2), and `restore_lost_decisions` is among `alarms`.
     */
    lostDecisions: z.array(z.object({ origin: z.string(), fromSeq: z.int().positive(), throughSeq: z.int().positive(), lastReportedAt: IsoInstant })),
    /**
     * The node's LAN certificate, as its newest beacon measured it: what the proxy serves (the cloud's issuer's, `issued`;
     * its dev CA's, `dev`; null: the probe failed), until when, whether a new one waits for the proxy to load it, and the
     * issued one's end (null: none from the cloud yet). Null when the beacon carries no `tls` (an older agent).
     */
    tls: z.object({
      served: z.enum(['issued', 'dev']).nullable(), servedNotAfter: IsoInstant.nullable(), reloadPending: z.boolean().nullable(), issuedNotAfter: IsoInstant.nullable(),
    }).nullable(),
    /** Which parts the newest beacon's agent sends (1: `queues`); 0 for an agent before it (JOB-S4). Optional: older servers. */
    beaconVersion: z.int().nonnegative().optional(),
    /** The node's queues from its newest beacon; null when it carries none (an agent before beaconVersion 1). Optional: older servers. */
    queues: QueueHealthView.nullable().optional(),
    /**
     * JOB-S4b: since when the node's agent, one that sends `beaconVersion` (so it can measure its queues), has sent no
     * `queues` part in an unbroken run of beacons up to the newest; null when the newest carries them, or comes from an
     * agent before them. An agent forgives a failed queues read only in its first ten minutes (beacon version 0), so a
     * run longer than that is an agent restarting in a loop, or a read that keeps failing. Optional: older servers.
     */
    queuesUnreadSince: IsoInstant.nullable().optional(),
  })
  .meta({ id: 'NodeHealthView' });

export const NodeHealth = defineQuery({
  name: 'fleet.nodes.health',
  summary: "The location's nodes, each with its newest health beacon's alarms and disk use (Radish staff).",
  input: z.object({ locationId: LocationId }),
  output: z.object({ nodes: z.array(NodeHealthView) }),
  permission: 'settings.platform.manage',
  availability: 'cloud',
  cloudReason: 'Beacons are what the node sends the cloud; the cloud is where a node is watched from.',
  scope: 'location',
  errors: [],
  agent: { expose: false },
});

/**
 * One unit's part in a frozen location's drain proof (lane X, drain gap (a), cutover row C3). A unit is `drained` when a
 * receiver took a heartbeat from it at or after `since` (the freeze) with its origin head at or below the cloud's
 * watermark for its replica. `heardVia` says whether the newest heartbeat was the cloud's own or a node's (sent up in its
 * beacon). The verdict is `@radish/sync`'s `judgeDrain`.
 */
export const DrainUnitView = z
  .object({
    deviceId: DeviceId, deviceName: z.string(), replica: z.string(), heartbeatAt: IsoInstant.nullable(), originSeqHead: z.int().nonnegative().nullable(),
    heardVia: z.enum(['cloud', 'node']).nullable(), cloudWatermark: z.int().nonnegative(),
    verdict: z.enum(['drained', 'never_answered', 'no_heartbeat_since', 'head_above_cloud']),
  })
  .meta({ id: 'DrainUnitView' });

export const NodeDrain = defineQuery({
  name: 'fleet.nodes.drain',
  summary: "Whether every unit of a frozen location has handed the cloud everything it recorded since `since`, naming the units that have not answered (Radish staff).",
  input: z.object({ locationId: LocationId, since: IsoInstant }),
  output: z.object({ since: IsoInstant, drained: z.boolean(), units: z.array(DrainUnitView), unanswered: z.array(z.string()) }),
  permission: 'settings.platform.manage',
  availability: 'cloud',
  cloudReason: "The proof compares each unit's head with the cloud's own watermark; only the cloud holds that.",
  scope: 'location',
  errors: [],
  agent: { expose: false },
});

// ---- Commander's fleet lists (lane CM item 3a; ops repos/commander/plan.md M1/M2-lite) ---------------------------------
// Radish staff's view across every account: legacy Commander's POS Devices, KDS Devices and Locations pages, and each
// location node's health. Cloud only, Radish staff only (`fleet.read`, never a merchant role or a reseller), paged on the
// server (legacy loaded the whole fleet in one call, Q-CM-13), and every call writes an audit entry naming the staff
// member and what they listed (coordinator 2026-10-15). No gateway key or other secret is in any row (legacy sent the
// Pay API key with every location, Q-CM-02).

const FLEET_CLOUD = "Radish staff watch every store's devices from the cloud, which receives every unit's heartbeat and every node's beacon.";

/** A page of a staff list: legacy's tables show 20 rows a page. */
const StaffPage = z.object({ offset: z.int().nonnegative().max(100_000).default(0), limit: z.int().positive().max(200).default(20) });

/** Legacy POS Devices' Online and Offline filter boxes, both ticked by default (field note commander.devices). */
/**
 * Which devices a list shows: connected, not connected, and (lane Q, coordinator-nodded 2026-10-17; redesign §4.4)
 * records no unit was ever heard from, "Never set up", which are hidden unless asked for and counted instead.
 */
const OnlineFilter = z.object({ online: z.boolean().default(true), offline: z.boolean().default(true), neverSetUp: z.boolean().default(false) });

/** Words matched case-insensitively within the row's text, as legacy's Keyword Search. */
const StaffSearch = z.string().trim().max(100);

/**
 * One device on Commander's POS Devices or KDS Devices page. `online` is the core rule `isUnitOnlineVia` over
 * `lastHeardAt` (activity, a heartbeat or a pull) at the newer of the cloud and the location node's beacon: within 55 s
 * when the cloud heard it last (its pull's arrival, lane X 2026-10-17), within three minutes through its hub, and never
 * while its hub is offline;
 * `appVersion` is the version the unit's newest cloud heartbeat declared (null when it only reached a node). Legacy's
 * Serial column has no counterpart yet: units report their hardware model, not a serial.
 */
export const StaffDeviceView = z
  .object({
    deviceId: DeviceId, accountId: z.uuid(), locationId: LocationId, locationName: z.string(), deviceName: z.string(), formFactor: FormFactor,
    hardwareModel: z.string().nullable(), appVersion: z.string().nullable(), lastHeardAt: IsoInstant.nullable(), heardVia: z.enum(['cloud', 'node']).nullable(),
    online: z.boolean(), pendingCount: z.int().nonnegative().nullable(), gateway: z.string().nullable(),
    /**
     * Commander's words for it (core unitConnection; lane X, 2026-10-17): `hub_offline` while the store's hub is offline,
     * so a register behind it reads "Can't reach Radish" rather than a stale Connected. Absent from an older server.
     */
    connection: z.enum(['connected', 'not_connected', 'hub_offline', 'never']).optional(),
    /** The store's IANA time zone, so Commander shows times in the store's own time (redesign, coordinator 2026-10-17). */
    timezone: z.string(),
    /**
     * Since when the register is in Training mode, from its newest heartbeat at whichever receiver heard it last (the
     * cloud, or its node's beacon); null when it is not (ops plan 2026-10-19-register-training-mode §7, IMPROVE L716).
     * Absent from an older server.
     */
    trainingSince: IsoInstant.nullable().optional(),
    /**
     * A KDS screen's printer, as legacy's KDS Devices page showed it (its Printer column; ops plan
     * 2026-10-18-kds-parity-slices slice 4 A5): the printer its link names, or null with none (legacy's yellow "no
     * printer assigned"), a link to a deleted printer, another location's, or one that is not a KDS printer counting as
     * none (B541's rule). Absent on other rows and from an older server.
     */
    kdsPrinter: z.object({ name: z.string() }).nullable().optional(),
  })
  .meta({ id: 'StaffDeviceView' });

export const StaffListDevices = defineQuery({
  name: 'fleet.staff.devices.list',
  summary: "Every store's device records across the platform for Radish staff (Commander's POS Devices and KDS Devices), or one location's, with whether each is online and its app version, a page at a time.",
  input: z.object({
    formFactors: z.array(FormFactor).max(5).optional(), search: StaffSearch.optional(), show: OnlineFilter.default({ online: true, offline: true, neverSetUp: false }), page: StaffPage.default({ offset: 0, limit: 20 }),
    /** One location's devices only (Commander's location view). */
    locationId: LocationId.optional(),
  }),
  output: z.object({
    devices: z.array(StaffDeviceView),
    /** The rows shown across every page (paging counts these). */
    matching: z.int().nonnegative(),
    /**
     * Never-set-up rows that matched but were hidden (`show.neverSetUp` false), for "15 never set up, hidden". Defaults to
     * 0 when a server older than this field answers, so a newer Commander still reads it (lane Q, 2026-10-17).
     */
    neverSetUp: z.int().nonnegative().default(0),
    asOf: IsoInstant,
  }),
  permission: 'fleet.read',
  availability: 'cloud',
  cloudReason: FLEET_CLOUD,
  scope: 'platform',
  errors: [],
  agent: { expose: false },
  legacy: ['GET /v1/commander/devices', 'GET /v1/commander/kds-devices'],
});

/**
 * One location on Commander's Locations page. `gateway` names the in-store card processor and its environment, never a
 * key; `channel` is the release channel, Production until OTA channels exist (legacy shows Production when unset);
 * `system` is whether the location runs on legacy or Radish Next (staff see both systems, doc 15).
 */
export const StaffLocationView = z
  .object({
    locationId: LocationId, accountId: z.uuid(), accountName: z.string(), name: z.string(), email: z.string().nullable(),
    programType: z.enum(['traditional', 'non_cash_adjustment', 'true_margin']), gateway: z.string().nullable(),
    channel: z.enum(['production', 'stable', 'canary', 'beta', 'dev']), system: z.enum(['legacy', 'next']),
    /** The store's IANA time zone (redesign, coordinator 2026-10-17). */
    timezone: z.string(),
  })
  .meta({ id: 'StaffLocationView' });

export const StaffListLocations = defineQuery({
  name: 'fleet.staff.locations.list',
  summary: "Every open location across the platform for Radish staff (Commander's Locations page), or one: name, email, program type, card processor and release channel, a page at a time.",
  input: z.object({
    search: StaffSearch.optional(), page: StaffPage.default({ offset: 0, limit: 20 }),
    /** That one location only (Commander's location view). */
    locationId: LocationId.optional(),
  }),
  output: z.object({ locations: z.array(StaffLocationView), matching: z.int().nonnegative() }),
  permission: 'fleet.read',
  availability: 'cloud',
  cloudReason: FLEET_CLOUD,
  scope: 'platform',
  errors: [],
  agent: { expose: false },
  legacy: ['GET /v1/commander/locations'],
});

/**
 * Whether a store's hub is reachable from Radish now (core hubReach; lane X, 2026-10-17): heard within 55 s (`online`),
 * silent longer (`offline`), silent but the cloud only just restarted (`checking`, shown as "Checking…", never as
 * offline), or never heard (`never`).
 */
export const HubReachState = z.enum(['online', 'offline', 'checking', 'never']).meta({ id: 'HubReachState' });

/**
 * One location node's newest health, with the location and account it serves, and its reach: `lastHeardAt` is the
 * cloud's own receive time of the newest thing it sent (a beacon, or a held poll's or pull's arrival), never the hub's
 * clock. Both are absent from a server older than them: a reader then judges the hub by `beaconAt` (core
 * hubReachFromBeacon), never as never-connected.
 */
export const StaffNodeView = NodeHealthView.extend({
  locationId: LocationId, locationName: z.string(), accountId: z.uuid(), accountName: z.string(),
  lastHeardAt: IsoInstant.nullable().optional(), reach: HubReachState.optional(),
})
  .meta({ id: 'StaffNodeView' });

/** One hub's reach, for Commander's 5 s status read. */
export const HubReachView = z
  .object({ nodeId: z.uuid(), serial: z.string(), accountId: z.uuid(), locationId: LocationId, lastHeardAt: IsoInstant.nullable(), reach: HubReachState })
  .meta({ id: 'HubReachView' });

export const StaffHubsReach = defineQuery({
  name: 'fleet.staff.hubs.reach',
  summary: "Every claimed hub's reach now for Radish staff (Commander's status dots, read every 5 s while a page is open): online, offline, checking or never, with when the cloud last heard it. Served from a 2 s snapshot shared by every staff caller.",
  input: z.object({ locationId: LocationId.optional() }),
  output: z.object({ hubs: z.array(HubReachView), asOf: IsoInstant }),
  permission: 'fleet.read',
  availability: 'cloud',
  cloudReason: FLEET_CLOUD,
  scope: 'platform',
  errors: [],
  agent: { expose: false },
});

export const StaffListNodes = defineQuery({
  name: 'fleet.staff.nodes.list',
  summary: "Every location node's newest health beacon for Radish staff (Commander's node health), or one location's.",
  input: z.object({ locationId: LocationId.optional() }),
  output: z.object({ nodes: z.array(StaffNodeView) }),
  permission: 'fleet.read',
  availability: 'cloud',
  cloudReason: FLEET_CLOUD,
  scope: 'platform',
  errors: [],
  agent: { expose: false },
});

// ---- Commander's location timeline (lane CM, M7-lite; coordinator-nodded design 2026-10-16) --------------------------
// One location's moments, newest first, from what the platform already stores: the node's beacons (alarms raised and
// cleared, quiet and back, release changes, its units quiet and back), Commander's device commands and node checks,
// devices activated and revoked, the cutover's steps, the node claimed and its certificate. Never an alert's message,
// a check's output, collect-info's values or anything about orders, customers, employees or payments.

/** What a timeline entry is about. */
export const TimelineKind = z.enum([
  'node_alarm_raised', 'node_alarm_cleared', 'node_quiet', 'node_back', 'node_release_changed', 'unit_quiet', 'unit_back',
  'command_sent', 'command_delivered', 'command_completed', 'command_failed', 'command_expired',
  'diagnostic_requested', 'diagnostic_completed', 'device_activated', 'device_revoked',
  'cutover_held', 'cutover_step', 'cutover_switched', 'node_claimed', 'node_certificate_issued',
]).meta({ id: 'TimelineKind' });

/** The timeline's filter chips: the node, devices, commands (device commands and node checks), the cutover. */
export const TimelineGroup = z.enum(['node', 'devices', 'commands', 'cutover']).meta({ id: 'TimelineGroup' });

/** A timeline entry's id cap, which its cursor shares: an account-wide moment's id runs to about 150 characters. */
const TIMELINE_ID_MAX = 200;

/**
 * Where the next (older) page starts: the last entry's time and id. The id takes any entry's id (it once capped at 120,
 * below an account-wide quiet-unit moment's ~150, and the page that ended on one failed: Commander, 2026-10-20).
 */
export const TimelineCursor = z.object({ at: IsoInstant, id: z.string().max(TIMELINE_ID_MAX) }).meta({ id: 'TimelineCursor' });

export const TimelineEntry = z
  .object({
    /** Stable for one moment (its source and kind), so a page boundary never repeats or drops one. */
    id: z.string().max(TIMELINE_ID_MAX),
    at: IsoInstant,
    kind: TimelineKind,
    group: TimelineGroup,
    /** The server's words for it ("Reload sent to Bar Register by Ada Admin"). */
    words: z.string().max(240),
    subject: z.discriminatedUnion('kind', [
      z.object({ kind: z.literal('device'), deviceId: DeviceId, deviceName: z.string() }),
      z.object({ kind: z.literal('node'), nodeId: z.uuid(), serial: z.string() }),
    ]).nullable(),
    /** Who did it: a Radish staff member's name, "a store user" or "the system"; null for what nobody did. */
    actor: z.string().nullable(),
    /** A cutover step recorded for the whole account, which has other locations too ("Account cutover: …"). */
    accountWide: z.boolean(),
    /** The store it happened at, on an account's timeline (which spans its stores); null on one location's. */
    locationName: z.string().nullable().default(null),
  })
  .meta({ id: 'TimelineEntry' });

/** The most stores an account's timeline reads (the rest are left out, worst-first ordering aside: by name). */
export const ACCOUNT_TIMELINE_STORES = 50;

/** How far back the timeline reads: a day by default, a week at most (coordinator, 2026-10-16). */
export const TIMELINE_WINDOW_HOURS = { default: 24, max: 168 } as const;
export const TIMELINE_PAGE = 100;

export const StaffLocationTimeline = defineQuery({
  name: 'fleet.staff.location.timeline',
  summary: "One location's or one account's timeline for Radish staff (Commander's store page, account dashboard and device page): hub alarms, quiet spells and releases, devices' commands and comings and goings, and the cutover, newest first, a page at a time; optionally one device's moments only.",
  input: z.object({
    /** Exactly one of locationId and accountId: one store, or every open store of an account (at most ACCOUNT_TIMELINE_STORES). */
    locationId: LocationId.optional(),
    accountId: z.uuid().optional(),
    /** Only this device's moments (Commander's device page), within the location or account named. */
    deviceId: DeviceId.optional(),
    windowHours: z.int().min(1).max(TIMELINE_WINDOW_HOURS.max).default(TIMELINE_WINDOW_HOURS.default),
    groups: z.array(TimelineGroup).min(1).max(4).optional(),
    before: TimelineCursor.optional(),
  }).refine((i) => (i.locationId === undefined) !== (i.accountId === undefined), { message: 'name exactly one of locationId and accountId' }),
  output: z.object({ entries: z.array(TimelineEntry), next: TimelineCursor.nullable(), asOf: IsoInstant }),
  permission: 'fleet.read',
  availability: 'cloud',
  cloudReason: FLEET_CLOUD,
  scope: 'platform',
  errors: [],
  agent: { expose: false },
});

// ---- Commander's Who needs help (ops plan 2026-10-17-commander-redesign §4.2/§4.7; coordinator-nodded 2026-10-17) ----
// The stores with a problem now, worst first (open cases come from support.cases.list, under its own permission), from core's `storeGlance` (the same rule the store page and
// Claude use). Structured, never finished English: Commander's renderer words each problem in the store's own time.
// The Problem union is closed but versioned (`version`), so adding a kind later (printer offline) is additive.

/** core STORE_GLANCE_VERSION: the problem kinds this answer may hold. */
export const STORE_GLANCE_CONTRACT_VERSION = 2;

export const StoreSeverity = z.enum(['needs_attention', 'keep_an_eye', 'fine']).meta({ id: 'StoreSeverity' });

export const StoreProblem = z.discriminatedUnion('kind', [
  /** Version 2 (lane X, 2026-10-17): the hub is offline (core hubReach, 55 s), since the cloud last heard it (null: never). */
  z.object({ kind: z.literal('hub_offline'), since: IsoInstant.nullable() }),
  /** Deprecated (version 1, the 5-minute beacon rule): no longer produced, kept so an older answer still parses. */
  z.object({ kind: z.literal('hub_not_checking_in'), since: IsoInstant.nullable() }),
  z.object({ kind: z.literal('register_not_connected'), deviceName: z.string(), since: IsoInstant }),
  z.object({ kind: z.literal('all_registers_off'), count: z.int().positive(), since: IsoInstant }),
  z.object({ kind: z.literal('hub_alarm'), alarm: z.string() }),
  z.object({ kind: z.literal('uploads_waiting'), count: z.int().positive(), oldestSeconds: z.int().nonnegative().nullable() }),
]).meta({ id: 'StoreProblem' });
export type StoreProblem = z.output<typeof StoreProblem>;

export const StoreGlanceView = z
  .object({
    version: z.int().positive(),
    severity: StoreSeverity,
    /** Worst first. */
    problems: z.array(StoreProblem),
    /** Which sentence describes the store: fine, one problem (the first), or several (the first, and a count). */
    sentence: z.enum(['fine', 'one_problem', 'several_problems']),
    registersInUse: z.int().nonnegative(),
    registersConnected: z.int().nonnegative(),
    neverSetUp: z.int().nonnegative(),
  })
  .meta({ id: 'StoreGlanceView' });
export type StoreGlanceView = z.output<typeof StoreGlanceView>;

export const AttentionStore = z
  .object({ accountId: z.uuid(), accountName: z.string(), locationId: LocationId, locationName: z.string(), timezone: z.string(), glance: StoreGlanceView })
  .meta({ id: 'AttentionStore' });

/** The most stores with a problem one answer lists; the fine ones are only counted. */
export const ATTENTION_LIMIT = { default: 50, max: 200 } as const;

export const StaffAttentionList = defineQuery({
  name: 'fleet.staff.attention.list',
  summary: "Who needs help, for Radish staff (Commander's home): every store with a problem now, worst first, each with its glance (severity, problems and sentence key); stores that are fine are counted, not listed. Open cases come from support.cases.list, under its own permission.",
  input: z.object({ limit: z.int().min(1).max(ATTENTION_LIMIT.max).default(ATTENTION_LIMIT.default) }),
  output: z.object({
    stores: z.array(AttentionStore),
    /** Stores with a problem beyond `limit`, not listed. */
    moreStores: z.int().nonnegative(),
    /**
     * Training stores with a problem, worst first, kept apart from `stores` (core whoNeedsHelp; lane Q, 2026-10-17), at
     * most `limit`. Empty from a server older than this field.
     */
    trainingStores: z.array(AttentionStore).default([]),
    /** Stores that are fine (open, with no problem now). */
    fineStores: z.int().nonnegative(),
    asOf: IsoInstant,
  }),
  permission: 'fleet.read',
  availability: 'cloud',
  cloudReason: FLEET_CLOUD,
  scope: 'platform',
  errors: [],
  agent: { expose: false },
});

// ---- Commander's Accounts and an account's dashboard (ops plan 2026-10-17-commander-redesign §4.2/§4.5a, step 5a;
// coordinator-nodded 2026-10-17, with lane X2's snapshot conditions) ------------------------------------------------------
// Every merchant account with its stores judged by core's `storeGlance` and the account by `accountGlance` (the worst
// store decides). Partner is the account's reseller; plans are not recorded yet, so `plan` is null ("—"). Open cases
// are counted only for a caller who also holds support.case.read (else null), and are never cached. `asOf` is the time
// of the fleet snapshot the glances were judged at (up to 15 s old).

/** core ACCOUNT_GLANCE_VERSION: the sentences this answer may hold. */
export const ACCOUNT_GLANCE_CONTRACT_VERSION = 1;

export const AccountGlanceView = z
  .object({
    version: z.int().positive(),
    severity: StoreSeverity,
    /** Every store fine, one store not fine (named), several (the worst named, and a count), or no open stores. */
    sentence: z.enum(['fine', 'one_store', 'several_stores', 'no_stores']),
    storesNeedingAttention: z.int().nonnegative(),
    storesToKeepAnEyeOn: z.int().nonnegative(),
    storesFine: z.int().nonnegative(),
    worstStore: z.string().nullable(),
  })
  .meta({ id: 'AccountGlanceView' });
export type AccountGlanceView = z.output<typeof AccountGlanceView>;

export const StaffAccountView = z
  .object({
    accountId: z.uuid(), accountName: z.string(),
    /** The account's reseller (partner). */
    partnerName: z.string().nullable(),
    /** The account's plan: not recorded yet, so always null until billing records plans. */
    plan: z.null(),
    /** Whether the account runs on legacy or Radish Next. */
    system: z.enum(['legacy', 'next']),
    storeCount: z.int().nonnegative(),
    glance: AccountGlanceView,
    /** Open and waiting-on-merchant cases; null unless the caller also holds support.case.read. */
    openCases: z.int().nonnegative().nullable(),
  })
  .meta({ id: 'StaffAccountView' });
export type StaffAccountView = z.output<typeof StaffAccountView>;

export const StaffAccountNotFound = defineError({
  code: 'fleet.account_not_found', httpStatus: 404, message: 'errors.fleet.account_not_found', retryable: false,
  details: z.object({ accountId: z.uuid() }),
});

/** Commander's Accounts filters; open_cases needs support.case.read. */
export const StaffAccountsFilter = z.enum(['needs_attention', 'open_cases', 'on_legacy']).meta({ id: 'StaffAccountsFilter' });

export const StaffAccountsList = defineQuery({
  name: 'fleet.staff.accounts.list',
  summary: "Commander's Accounts, for Radish staff: every open merchant account with its partner, system, store count and glance (the worst store decides), and its open cases when the caller may read cases; worst first, then by name, a page at a time.",
  input: z.object({
    search: StaffSearch.optional(),
    filter: StaffAccountsFilter.optional(),
    page: z.object({ offset: z.int().nonnegative().max(100_000).default(0), limit: z.int().min(1).max(100).default(50) }).default({ offset: 0, limit: 50 }),
  }),
  output: z.object({ accounts: z.array(StaffAccountView), matching: z.int().nonnegative(), asOf: IsoInstant }),
  permission: 'fleet.read',
  availability: 'cloud',
  cloudReason: FLEET_CLOUD,
  scope: 'platform',
  errors: [],
  agent: { expose: false },
});

/** The most stores and devices one account answer lists (the rest are counted). */
export const ACCOUNT_DASHBOARD_LIMIT = { stores: 200, devices: 500 } as const;

export const StaffAccountGet = defineQuery({
  name: 'fleet.staff.account.get',
  summary: "One account's dashboard for Radish staff: the account with its glance, each open store with its glance (worst first), and its devices (bounded); its cases come from support.cases.list and its activity from fleet.staff.location.timeline.",
  input: z.object({ accountId: z.uuid() }),
  output: z.object({
    account: StaffAccountView,
    stores: z.array(z.object({ location: StaffLocationView, glance: StoreGlanceView })),
    moreStores: z.int().nonnegative(),
    devices: z.array(StaffDeviceView),
    moreDevices: z.int().nonnegative(),
    asOf: IsoInstant,
  }),
  permission: 'fleet.read',
  availability: 'cloud',
  cloudReason: FLEET_CLOUD,
  scope: 'platform',
  errors: [StaffAccountNotFound],
  agent: { expose: false },
});

// ---- Commander's device page (ops plan 2026-10-17-commander-redesign §4.2/§4.5a, step 5a; coordinator-nodded
// 2026-10-17) -------------------------------------------------------------------------------------------------------
// One register at a glance (core `registerGlance`: never heard is never set up, not offline) with its newest collect-info
// answer as its stats. Sales today, running since and card terminal status are not reported yet (the page shows "—").
// Its own day is fleet.staff.location.timeline with `deviceId`.

/** core REGISTER_GLANCE_VERSION: the problem kinds this answer may hold. */
export const REGISTER_GLANCE_CONTRACT_VERSION = 1;

export const RegisterProblem = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('not_connected'), since: IsoInstant }),
  z.object({ kind: z.literal('uploads_waiting'), count: z.int().positive() }),
  // Named for what they measure: the disk's size and the battery's charge level (0–100), not money words (A5).
  z.object({ kind: z.literal('storage_low'), freeBytes: z.number().nonnegative(), diskBytes: z.number().positive() }),
  z.object({ kind: z.literal('battery_low'), level: z.number().min(0).max(100) }),
]).meta({ id: 'RegisterProblem' });

export const RegisterGlanceView = z
  .object({
    version: z.int().positive(),
    state: z.enum(['never_set_up', 'connected', 'not_connected']),
    severity: StoreSeverity,
    /** Worst first. */
    problems: z.array(RegisterProblem),
    sentence: z.enum(['never_set_up', 'fine', 'one_problem', 'several_problems']),
  })
  .meta({ id: 'RegisterGlanceView' });
export type RegisterGlanceView = z.output<typeof RegisterGlanceView>;

export const StaffDeviceGet = defineQuery({
  name: 'fleet.staff.device.get',
  summary: "One device's page for Radish staff (Commander): the device with its glance (connected, not connected since, never set up, and what is worth an eye) and its newest collect-info answer as its stats.",
  input: z.object({ deviceId: DeviceId }),
  output: z.object({
    device: StaffDeviceView,
    /** The device's account's name, for the page's breadcrumb. */
    accountName: z.string(),
    glance: RegisterGlanceView,
    /** Its newest collect-info answer, or null when it never gave one (the page offers "Check it now"). */
    lastInfo: z.object({ answeredAt: IsoInstant, info: z.array(z.object({ key: DeviceInfoKey, value: z.string() })) }).nullable(),
    asOf: IsoInstant,
  }),
  permission: 'fleet.read',
  availability: 'cloud',
  cloudReason: FLEET_CLOUD,
  scope: 'platform',
  errors: [DeviceNotFound],
  agent: { expose: false },
});

// ---- Commander's Equipment (ops plan 2026-10-17-commander-redesign §4.5b, step 5c; coordinator-nodded 2026-10-17) ----
// One record per physical item Radish ships: what it is, its serial, where it is (a store, or in stock), its status,
// and what it became (a register's device record, a hub's node, a router's RMS device). Radish staff only; written by
// these commands in their own transaction, read across accounts by the list. Places, custody and kits come in 5e.

export const EquipmentKind = z.enum(['register', 'handheld', 'kds', 'card_terminal', 'printer', 'hub', 'router']).meta({ id: 'EquipmentKind' });
/** checked_out: with a Radish staff member (step 5e's check-out). */
export const EquipmentStatus = z.enum(['in_stock', 'installed', 'out_for_repair', 'checked_out', 'retired']).meta({ id: 'EquipmentStatus' });
/** As typed on the label (stored as typed, compared without case). */
const EquipmentSerial = z.string().trim().min(1).max(64).regex(/^[A-Za-z0-9 ._/-]+$/);
const EquipmentWhere = z.object({ accountId: z.uuid(), locationId: LocationId });

export const EquipmentSerialTaken = defineError({
  code: 'fleet.equipment_serial_taken', httpStatus: 409, message: 'errors.fleet.equipment_serial_taken', retryable: false,
  details: z.object({ kind: EquipmentKind, serial: z.string() }),
});
export const EquipmentNotFound = defineError({
  code: 'fleet.equipment_not_found', httpStatus: 404, message: 'errors.fleet.equipment_not_found', retryable: false,
  details: z.object({ equipmentId: z.uuid() }),
});
export const EquipmentRetired = defineError({
  code: 'fleet.equipment_retired', httpStatus: 409, message: 'errors.fleet.equipment_retired', retryable: false,
  details: z.object({ equipmentId: z.uuid() }),
});
export const EquipmentLinkTaken = defineError({
  code: 'fleet.equipment_link_taken', httpStatus: 409, message: 'errors.fleet.equipment_link_taken', retryable: false,
  details: z.object({ equipmentId: z.uuid() }),
});
/** A link names a device record or hub that doesn't exist (coordinator 2026-10-17, #1251). */
export const EquipmentLinkNotFound = defineError({
  code: 'fleet.equipment_link_not_found', httpStatus: 404, message: 'errors.fleet.equipment_link_not_found', retryable: false,
  details: z.object({ link: z.enum(['device', 'node']), id: z.uuid() }),
});
/** An installed item's link names a device record or hub at another store (or none): it needs a look, never a silent move. */
export const EquipmentLinkElsewhere = defineError({
  code: 'fleet.equipment_link_elsewhere', httpStatus: 409, message: 'errors.fleet.equipment_link_elsewhere', retryable: false,
  details: z.object({ link: z.enum(['device', 'node']), id: z.uuid() }),
});
/** An item in a lab kit moves only with its kit (step 5e). */
export const EquipmentInKit = defineError({
  code: 'fleet.equipment_in_kit', httpStatus: 409, message: 'errors.fleet.equipment_in_kit', retryable: false,
  details: z.object({ equipmentId: z.uuid(), kitId: z.uuid() }),
});
/**
 * B1778 review N3 and round 2 B1: unclaiming the hub would strand facts it decided, since an unclaimed hub's uploads are
 * refused. A hub still heard is refused only while its newest beacon shows facts the cloud lacks (`fromSeq`..`throughSeq`
 * of its own origin); it uploads them within a pass, then retire again. A silent hub (`heard` false) is always refused
 * unless staff give up whatever it holds (`abandonUnpushedFacts`): its last beacon can't show what it decided after
 * `heardAt`, so the range is only what that beacon showed (null when nothing or, `headKnown` false, unknown). The audit
 * entry records what was given up.
 */
export const EquipmentHubUnpushedFacts = defineError({
  code: 'fleet.equipment_hub_unpushed_facts', httpStatus: 409, message: 'errors.fleet.equipment_hub_unpushed_facts', retryable: true,
  details: z.object({
    equipmentId: z.uuid(), nodeId: z.uuid(), fromSeq: z.int().positive().nullable(), throughSeq: z.int().positive().nullable(), headKnown: z.boolean(),
    heardAt: IsoInstant.nullable(), heard: z.boolean(),
  }),
});
export const EquipmentLocationNotFound = defineError({
  code: 'fleet.equipment_location_not_found', httpStatus: 404, message: 'errors.fleet.equipment_location_not_found', retryable: false,
  details: z.object({ locationId: LocationId }),
});

const EQUIPMENT_CLOUD = "Radish's equipment inventory is platform-wide and kept in the cloud, never at a store.";

export const RegisterEquipment = defineCommand({
  name: 'fleet.equipment.register',
  summary: "Record a physical item Radish ships (a register, kitchen screen, card terminal, printer, hub or router): its serial, model, and either in stock or installed at a store.",
  input: z.object({
    /** Minted by the caller (UUIDv7), so a retried register replays instead of making a second record. */
    equipmentId: z.uuid(),
    kind: EquipmentKind,
    serial: EquipmentSerial,
    model: z.string().trim().min(1).max(80),
    status: z.enum(['in_stock', 'installed']),
    /** Required when installed; never given when in stock. */
    where: EquipmentWhere.optional(),
    notes: z.string().trim().max(500).optional(),
  }).refine((i) => (i.status === 'installed') === (i.where !== undefined), { message: 'an installed item names its store; an item in stock names none' }),
  output: z.object({ equipmentId: z.uuid() }),
  permission: 'equipment.manage',
  risk: 'low',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: EQUIPMENT_CLOUD,
  emits: [],
  errors: [EquipmentSerialTaken, EquipmentLocationNotFound, CardNumberInText],
  dryRun: true,
  agent: { expose: false },
});

export const UpdateEquipment = defineCommand({
  name: 'fleet.equipment.update',
  summary: "Change an item's status (in stock, installed, out for repair), where it is installed, its notes, or what it is linked to (a device record, a hub, a router). A retired item can't be changed.",
  input: z.object({
    equipmentId: z.uuid(),
    status: z.enum(['in_stock', 'installed', 'out_for_repair']).optional(),
    /** Where it is installed; null clears it (in stock, out for repair). Installed needs one. */
    where: EquipmentWhere.nullable().optional(),
    notes: z.string().trim().max(500).nullable().optional(),
    model: z.string().trim().min(1).max(80).optional(),
    /** What it is linked to: set one, or null to unlink that one. A device record or hub must exist and, when the item is installed, be at its store. */
    link: z.object({ deviceId: DeviceId.nullable().optional(), nodeId: z.uuid().nullable().optional(), rmsDeviceId: z.string().trim().min(1).max(64).nullable().optional() }).optional(),
  }),
  output: z.object({ equipmentId: z.uuid(), status: EquipmentStatus }),
  permission: 'equipment.manage',
  risk: 'low',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: EQUIPMENT_CLOUD,
  emits: [],
  errors: [EquipmentNotFound, EquipmentRetired, EquipmentLinkTaken, EquipmentLinkNotFound, EquipmentLinkElsewhere, EquipmentLocationNotFound, EquipmentInKit, CardNumberInText],
  dryRun: true,
  agent: { expose: false },
});

export const RetireEquipment = defineCommand({
  name: 'fleet.equipment.retire',
  summary: "Take an item out of service for good, saying why. It stays in the inventory, listed behind the Retired filter.",
  input: z.object({
    equipmentId: z.uuid(), reason: z.string().trim().min(1).max(200),
    /** Retire a silent hub, giving up whatever it decided that the cloud never got (B1778 review N3, round 2 B1). */
    abandonUnpushedFacts: z.literal(true).optional(),
  }),
  output: z.object({ equipmentId: z.uuid() }),
  permission: 'equipment.manage',
  // Irreversible (coordinator 2026-10-17): if the Broker ever exposes it, it goes through approval.
  risk: 'medium',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: EQUIPMENT_CLOUD,
  emits: [],
  errors: [EquipmentNotFound, EquipmentRetired, EquipmentInKit, EquipmentHubUnpushedFacts],
  dryRun: true,
  agent: { expose: false },
});

export const EquipmentView = z
  .object({
    equipmentId: z.uuid(), kind: EquipmentKind, serial: z.string(), model: z.string(), status: EquipmentStatus,
    accountId: z.uuid().nullable(), accountName: z.string().nullable(), locationId: LocationId.nullable(), locationName: z.string().nullable(), timezone: z.string().nullable(),
    deviceId: DeviceId.nullable(), deviceName: z.string().nullable(), nodeId: z.uuid().nullable(), rmsDeviceId: z.string().nullable(),
    /** When its linked device or hub last checked in, as of the fleet snapshot, or its router as RMS reports it when RMS answers. */
    lastSeenAt: IsoInstant.nullable(),
    notes: z.string().nullable(),
    registeredAt: IsoInstant, registeredBy: z.string(),
    retiredAt: IsoInstant.nullable(), retireReason: z.string().nullable(),
    /** Where it is when not at a store (the office, out for repair, a staff member), and the lab kit it travels in (step 5e). */
    placeId: z.uuid().nullable(), placeName: z.string().nullable(), kitId: z.uuid().nullable(), kitName: z.string().nullable(),
  })
  .meta({ id: 'EquipmentView' });

export const StaffListEquipment = defineQuery({
  name: 'fleet.staff.equipment.list',
  summary: "Commander's Equipment, for Radish staff: every recorded item with what it is, its serial, where it is, its status and when it was last seen, searched and filtered, a page at a time. Retired items show only when asked for.",
  input: z.object({
    search: StaffSearch.optional(),
    kinds: z.array(EquipmentKind).min(1).max(7).optional(),
    /** Retired items only when named here. */
    statuses: z.array(EquipmentStatus).min(1).max(5).optional(),
    locationId: LocationId.optional(),
    /** Only what is at this place (the office, repair, or a staff member's). */
    placeId: z.uuid().optional(),
    page: z.object({ offset: z.int().nonnegative().max(100_000).default(0), limit: z.int().min(1).max(100).default(50) }).default({ offset: 0, limit: 50 }),
  }),
  output: z.object({ items: z.array(EquipmentView), matching: z.int().nonnegative(), asOf: IsoInstant }),
  permission: 'fleet.read',
  availability: 'cloud',
  cloudReason: FLEET_CLOUD,
  scope: 'platform',
  errors: [],
  // Claude in Commander reads a store's equipment through the Broker (coordinator 2026-10-17, #1252): read-only, and
  // like every Broker tool it names one store, so items in stock or out for repair aren't in its answer.
  agent: { expose: true, surfaces: ['staff.support'], description: "Name the store (locationId): answers that store's installed equipment with its serials, status and last check-in." },
});

// ---- Commander's typed device commands (lane CM 3b; repos/commander/plan.md §4.4) -------------------------------------
// Legacy Commander published `reload`, `sync` and `seek-info` straight to a device's Ably channel from the console, and
// toasted "Command sent successfully" whether or not any device heard it (bugs B468, B469). Here staff send a typed
// command that the cloud stores once per device, a unit fetches on its next pull and runs at most once before it
// expires, and answers on its next heartbeat; Commander shows each device's own status. Control, not a fact: no event,
// no sync item. Approved by the coordinator 2026-10-15 with lane X's design.

/**
 * How long a command waits for its unit: minutes, so a unit back from offline later never reloads mid-service, and at
 * least three, since a node takes its units' commands from its beacon about once a minute (lane X, 2026-10-15).
 */
export const DEVICE_COMMAND_TTL = { defaultSeconds: 300, minSeconds: 180, maxSeconds: 900 } as const;

export const SendDeviceCommand = defineCommand({
  name: 'fleet.sendDeviceCommand',
  summary: "Send reload, resync or collect-info to some of one location's devices from Commander; each device runs it once, on its next pull, unless it expires first.",
  input: z.object({
    /** The location's account: Radish staff act in it from Commander (`staffAnyLocation`). */
    accountId: z.uuid(),
    locationId: LocationId,
    deviceIds: z.array(DeviceId).min(1).max(50),
    command: CommanderCommandName,
    /** The support case this is done in (its location's): the case lists it. */
    caseId: z.uuid().optional(),
    ttlSeconds: z.int().min(DEVICE_COMMAND_TTL.minSeconds).max(DEVICE_COMMAND_TTL.maxSeconds).default(DEVICE_COMMAND_TTL.defaultSeconds),
  }),
  output: z.object({ commands: z.array(z.object({ deviceId: DeviceId, commandId: z.uuid(), expiresAt: IsoInstant })) }),
  permission: 'fleet.command.basic',
  availability: 'cloud',
  origin: 'any',
  cloudReason: "Commander runs in the cloud; a store's node takes its units' commands from there.",
  emits: [],
  errors: [DeviceNotFound, SupportCaseNotFound, SupportCaseClosed],
  risk: 'medium',
  dryRun: true,
  agent: { expose: false },
  staffAnyLocation: true,
  legacy: ['Ably reload', 'Ably sync', 'Ably seek-info'],
});

/** Commander's Alert: at most this many sends a minute to one location (coordinator, 2026-10-16). */
export const ALERT_RATE = { perMinute: 10 } as const;

const AlertTargets = {
  /** The location's account: Radish staff act in it from Commander (`staffAnyLocation`). */
  accountId: z.uuid(),
  locationId: LocationId,
  deviceIds: z.array(DeviceId).min(1).max(50),
  /** The support case this is done in (its location's): the case lists it. */
  caseId: z.uuid().optional(),
};

/**
 * Legacy Commander's Alert (field note commander.devices; legacy published `alert` to each device's Ably channel after
 * checking a password against a constant in the console, C1): a type and a message each selected register shows until
 * dismissed. Needs `fleet.command.alert` and a step-up within five minutes in this session (`fleet.step_up_required`
 * otherwise); at most ALERT_RATE.perMinute sends a minute per location. Each device receives it on its next pull, at
 * most once, within five minutes; a build that can't show alerts answers `unsupported`. The message lives only on the
 * command row, never in the audit entry.
 */
export const SendDeviceAlert = defineCommand({
  name: 'fleet.sendDeviceAlert',
  summary: "Show an alert (type and message) on some of one location's registers from Commander until it is dismissed; needs a recent step-up.",
  input: z.object({ ...AlertTargets, alert: DeviceAlert }),
  output: z.object({ commands: z.array(z.object({ deviceId: DeviceId, commandId: z.uuid(), expiresAt: IsoInstant })) }),
  permission: 'fleet.command.alert',
  availability: 'cloud',
  origin: 'any',
  cloudReason: "Commander runs in the cloud; a store's node takes its units' commands from there.",
  emits: [],
  errors: [DeviceNotFound, StepUpRequired, AlertRateLimited, SupportCaseNotFound, SupportCaseClosed],
  risk: 'medium',
  dryRun: true,
  agent: { expose: false },
  staffAnyLocation: true,
  legacy: ['Ably alert'],
});

/** Legacy Commander's Dismiss: every Commander alert on the selected registers goes away (confirmation only, as legacy). */
export const DismissDeviceAlerts = defineCommand({
  name: 'fleet.dismissDeviceAlerts',
  summary: "Remove every Commander alert from some of one location's registers.",
  input: z.object(AlertTargets),
  output: z.object({ commands: z.array(z.object({ deviceId: DeviceId, commandId: z.uuid(), expiresAt: IsoInstant })) }),
  permission: 'fleet.command.alert',
  availability: 'cloud',
  origin: 'any',
  cloudReason: "Commander runs in the cloud; a store's node takes its units' commands from there.",
  emits: [],
  errors: [DeviceNotFound, SupportCaseNotFound, SupportCaseClosed],
  risk: 'low',
  dryRun: true,
  agent: { expose: false },
  staffAnyLocation: true,
  legacy: ['Ably remove-all-alerts'],
});

/** One command's progress at its device, and what `collectInfo` answered. */
export const DeviceCommandView = z
  .object({
    commandId: z.uuid(), deviceId: DeviceId, command: DeviceCommandName, issuedAt: IsoInstant, expiresAt: IsoInstant,
    state: z.enum(['waiting', 'delivered', 'acknowledged', 'completed', 'failed', 'expired']),
    stateAt: IsoInstant, failure: z.enum(['expired', 'unsupported', 'error']).nullable(),
    info: z.array(z.object({ key: DeviceInfoKey, value: z.string() })),
  })
  .meta({ id: 'DeviceCommandView' });

export const DeviceCommandStatus = defineQuery({
  name: 'fleet.deviceCommands.status',
  summary: "Where each of some Commander device commands stands at its device (waiting, delivered, acknowledged, completed, failed or expired), with collect-info's answers.",
  input: z.object({ commandIds: z.array(z.uuid()).min(1).max(50) }),
  output: z.object({ commands: z.array(DeviceCommandView) }),
  permission: 'fleet.read',
  availability: 'cloud',
  cloudReason: FLEET_CLOUD,
  scope: 'platform',
  errors: [],
  agent: { expose: false },
});
